import { useState } from "react";
import { useParams } from "react-router-dom";
import { MutationError, ProblemError } from "@csa/web-foundation";
import { useT } from "../../i18n/index";
import { Icon } from "../../ui/icons";
import { useAppendEvent, useClaim, useResolve, useTransition } from "./use-claims";
import {
  APPENDABLE_EVENTS,
  NEXT_STATUS,
  RESOLVE_OUTCOMES,
  TERMINAL_STATUSES,
  type AppendableEvent,
  type Claim,
  type ClaimEvent,
  type ClaimStatus,
} from "./types";

const fmt = (iso: string) =>
  new Date(iso).toLocaleString("tr-TR", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });

// The claim's lifecycle as a calm k-status pill — never alarm-red.
const CLAIM_STATUS: Record<ClaimStatus, string> = {
  REPORTED: "pending",
  DOCUMENTING: "review",
  SUBMITTED: "review",
  IN_PROGRESS: "active",
  RESOLVED: "active",
  WITHDRAWN: "pending",
};

export function ClaimDetail() {
  const { id = "" } = useParams();
  const { t } = useT();
  const { data, isLoading, error } = useClaim(id);

  if (isLoading)
    return (
      <div className="k-loading">
        <div className="k-loading__head">
          <span className="k-loading__spinner" />
          <span className="k-loading__label">{t("claims.loading")}</span>
        </div>
      </div>
    );
  if (error || !data)
    return (
      <div className="k-note k-note--error">
        <div className="k-note__row">
          <span className="k-note__icon k-note__icon--error">
            <Icon name="alert-triangle" size={20} />
          </span>
          <p role="alert" className="k-note__desc">
            {error instanceof ProblemError ? error.problem.detail : t("claims.error")}
          </p>
        </div>
      </div>
    );

  const { claim, events } = data;
  const terminal = TERMINAL_STATUSES.includes(claim.status);
  return (
    <section className="surface claim-detail">
      <header className="surface__head">
        <div>
          <h1 className="surface__title">
            {claim.lineCode} · {claim.carrierId}
          </h1>
          <p className="surface__subtitle">
            {t("claims.status")}: <strong data-testid="claim-status">{claim.status}</strong> ·{" "}
            {t("claims.versionAtLoss")}: {claim.policyVersionId.slice(0, 8)} · {t("claims.loss")}{" "}
            {fmt(claim.lossAt)}
          </p>
          <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
            {/* SENSITIVE BOUNDARY: flags only — never the detail_ref content. */}
            {claim.involvesBodilyInjury && (
              <span className="k-badge k-badge--warning" data-testid="injury-badge">
                <Icon name="alert" size={14} />
                {t("claims.injury")}
              </span>
            )}
            {claim.hasThirdParty && (
              <span className="k-badge k-badge--standard">{t("claims.thirdParty")}</span>
            )}
          </div>
          {terminal && (
            <p
              data-testid="claim-outcome"
              className="k-status k-status--active"
              style={{ marginTop: 12 }}
            >
              <Icon name="check-circle" size={16} />
              {t("claims.outcome")}: <strong>{claim.outcome ?? claim.status}</strong>
              {claim.outcomeAmount ? ` · ${claim.outcomeAmount}` : ""}
              {claim.satisfactionScore ? ` · ★${claim.satisfactionScore}` : ""}
            </p>
          )}
        </div>
        <span className={`k-status k-status--${CLAIM_STATUS[claim.status] ?? "pending"}`}>
          <span className="k-status__dot" />
          {claim.status}
        </span>
      </header>

      <Timeline events={events} />

      {!terminal && (
        <div className="claim-actions">
          <Advance id={id} claim={claim} />
          <AppendEvent id={id} />
          {/* D6 — the whole Resolve card used to be gated on IN_PROGRESS, which
              matches the server rule for RESOLVED but NOT for WITHDRAWN: the
              service allows a withdrawal from ANY non-terminal state
              (claims.service.ts:303). A claim opened in error at REPORTED or
              DOCUMENTING therefore had no way to be closed from the console.
              The card now renders for every non-terminal claim; only the
              resolve half is IN_PROGRESS-gated, matching the API exactly. */}
          <Resolve id={id} canResolve={claim.status === "IN_PROGRESS"} />
        </div>
      )}
    </section>
  );
}

function Timeline({ events }: { events: ClaimEvent[] }) {
  const { t } = useT();
  const ordered = [...events].sort((a, b) => a.occurredAt.localeCompare(b.occurredAt));
  return (
    <section aria-label={t("claims.timeline")}>
      <p className="surface__section-label">{t("claims.timeline")}</p>
      <div className="k-card k-card--pad-sm">
        <ol className="claim-timeline" style={{ margin: 0, padding: 0, listStyle: "none" }}>
          {ordered.map((e, i) => (
            <li
              key={e.id}
              data-testid="timeline-event"
              style={{
                padding: "12px 0",
                borderTop: i === 0 ? "none" : "1px solid var(--k-hairline-2)",
              }}
            >
              <strong style={{ color: "var(--k-ink)" }}>{e.eventType}</strong>{" "}
              <span style={{ color: "var(--k-muted)" }}>· {fmt(e.occurredAt)}</span>
              {/* payload is the non-sensitive structured detail; rendered as plain pairs. */}
              {e.payload && Object.keys(e.payload).length > 0 && (
                <span style={{ color: "var(--k-muted)" }}>
                  {" "}
                  —{" "}
                  {Object.entries(e.payload)
                    .map(([k, v]) => `${k}: ${String(v)}`)
                    .join(", ")}
                </span>
              )}
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

function Advance({ id, claim }: { id: string; claim: Claim }) {
  const { t } = useT();
  const transition = useTransition(id);
  const next = NEXT_STATUS[claim.status];
  // The carrier reference for SUBMITTED used to come from globalThis.prompt?.(),
  // which returns undefined where prompt is unavailable — the advance was then a
  // dead click. Inline: the field appears only for the step that needs it.
  const [carrierRef, setCarrierRef] = useState("");
  if (!next) return null;
  const needsRef = next === "SUBMITTED";
  return (
    <div className="k-card k-card--pad-sm">
      {needsRef && (
        <label className="k-field" style={{ marginBottom: 8 }}>
          <span className="k-field__label">{t("claims.carrierRefPrompt")}</span>
          <input
            className="k-input"
            value={carrierRef}
            onChange={(e) => setCarrierRef(e.target.value)}
            data-testid="claim-carrier-ref"
          />
        </label>
      )}
      <button
        type="button"
        className="k-btn k-btn--primary"
        disabled={transition.isPending || (needsRef && !carrierRef.trim())}
        onClick={() =>
          transition.mutate(
            needsRef ? { toStatus: next, carrierClaimRef: carrierRef.trim() } : { toStatus: next },
          )
        }
      >
        {t("claims.advance")} → {next}
      </button>
      {/* A transition the server refuses (wrong state, missing grant) left the
          status pill exactly as it was, unexplained. */}
      <MutationError
        error={transition.error}
        fallback={t("cl.actionFailed")}
        offline={t("common.offline")}
      />
    </div>
  );
}

function AppendEvent({ id }: { id: string }) {
  const { t } = useT();
  const append = useAppendEvent(id);
  const [eventType, setEventType] = useState<AppendableEvent>("DOCUMENT_ADDED");
  const [note, setNote] = useState("");
  return (
    <form
      className="claim-event-form k-card"
      onSubmit={(e) => {
        e.preventDefault();
        /* setNote("") used to run unconditionally, so a REJECTED append destroyed
           what the advisor had typed — the one input that cannot be recovered by
           retrying, because it is gone from the field. Clearing now belongs to
           success, which is the only outcome that means "it is safely stored". */
        append.mutate(
          { eventType, payload: note.trim() ? { note: note.trim() } : undefined },
          { onSuccess: () => setNote("") },
        );
      }}
    >
      <p className="surface__section-label" style={{ marginTop: 0 }}>
        {t("claims.event.heading")}
      </p>
      <label className="k-field">
        <span className="k-field__label">{t("claims.event.type")}</span>
        <div className="k-select">
          <select
            className="k-select__native"
            aria-label={t("claims.event.type")}
            value={eventType}
            onChange={(e) => setEventType(e.target.value as AppendableEvent)}
          >
            {APPENDABLE_EVENTS.map((ev) => (
              <option key={ev} value={ev}>
                {ev}
              </option>
            ))}
          </select>
          <span className="k-select__chevron">
            <Icon name="chevron-down" size={16} />
          </span>
        </div>
      </label>
      <label className="k-field">
        <span className="k-field__label">{t("claims.event.note")}</span>
        <input
          className="k-input"
          aria-label={t("claims.event.note")}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </label>
      <button type="submit" className="k-btn k-btn--soft" disabled={append.isPending}>
        <Icon name="check" size={16} />
        {t("claims.event.add")}
      </button>
      <MutationError
        error={append.error}
        fallback={t("cl.actionFailed")}
        offline={t("common.offline")}
      />
    </form>
  );
}

function Resolve({ id, canResolve }: { id: string; canResolve: boolean }) {
  const { t } = useT();
  const resolve = useResolve(id);
  const [outcome, setOutcome] = useState<(typeof RESOLVE_OUTCOMES)[number]>("PAID");
  const [amount, setAmount] = useState("");
  const [satisfaction, setSatisfaction] = useState("");
  const [reason, setReason] = useState("");

  // GAP-AUDIT-3 D6 — the form's onSubmit was a bare preventDefault: three inputs
  // sat inside a <form> whose submit did NOTHING, so pressing Enter (the obvious
  // gesture with a focused field) silently discarded the gesture. The resolve is
  // the form's primary action, so submit now performs it; withdraw stays an
  // explicit secondary button, never something Enter can trigger by accident.
  // The outcome↔amount rule, mirrored from claims.service.ts:276-292 so Enter does not
  // become a one-keystroke 400: PAID / PARTIALLY_PAID require an amount > 0; REJECTED
  // takes none. The SERVER is the boundary and re-enforces — this only stops the
  // gesture from failing silently-looking in the advisor's face.
  const amountNum = amount.trim() ? Number(amount.trim()) : null;
  const amountOk =
    outcome === "REJECTED"
      ? amountNum === null || amountNum <= 0
      : amountNum !== null && amountNum > 0;

  function doResolve() {
    if (resolve.isPending || !canResolve || !amountOk) return;
    resolve.mutate({
      resolution: "RESOLVED",
      outcome,
      outcomeAmount: amount.trim() || undefined,
      satisfactionScore: satisfaction.trim() ? Number(satisfaction.trim()) : undefined,
    });
  }

  return (
    <form
      className="claim-resolve-form k-card"
      onSubmit={(e) => {
        e.preventDefault();
        doResolve();
      }}
    >
      <p className="surface__section-label" style={{ marginTop: 0 }}>
        {t("claims.resolve.heading")}
      </p>
      {/* RESOLVED is reachable only from IN_PROGRESS (claims.service.ts:259) —
          the carrier has to have acted before there is an outcome to record. */}
      {canResolve ? (
        <>
          <label className="k-field">
            <span className="k-field__label">{t("claims.resolve.outcome")}</span>
            <div className="k-select">
              <select
                className="k-select__native"
                aria-label={t("claims.resolve.outcome")}
                value={outcome}
                onChange={(e) => setOutcome(e.target.value as typeof outcome)}
              >
                {RESOLVE_OUTCOMES.map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
              <span className="k-select__chevron">
                <Icon name="chevron-down" size={16} />
              </span>
            </div>
          </label>
          <label className="k-field">
            <span className="k-field__label">{t("claims.resolve.amount")}</span>
            <input
              className="k-input"
              aria-label={t("claims.resolve.amount")}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </label>
          <label className="k-field">
            <span className="k-field__label">{t("claims.resolve.satisfaction")}</span>
            <input
              className="k-input"
              aria-label={t("claims.resolve.satisfaction")}
              value={satisfaction}
              onChange={(e) => setSatisfaction(e.target.value)}
            />
          </label>
          <button
            type="submit"
            className="k-btn k-btn--approve"
            data-testid="claim-resolve"
            disabled={resolve.isPending || !amountOk}
          >
            <Icon name="check-circle" size={16} />
            {t("claims.resolve.resolve")}
          </button>
          {!amountOk && (
            <p className="k-field__hint" data-testid="claim-amount-rule">
              {t(
                outcome === "REJECTED"
                  ? "claims.resolve.rejectedNoAmount"
                  : "claims.resolve.amountRequired",
              )}
            </p>
          )}
        </>
      ) : (
        <p className="k-field__hint" data-testid="claim-resolve-blocked">
          {t("claims.resolve.needsInProgress")}
        </p>
      )}
      <label className="k-field" style={{ marginTop: 18 }}>
        <span className="k-field__label">{t("claims.resolve.reason")}</span>
        <input
          className="k-input"
          aria-label={t("claims.resolve.reason")}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </label>
      <button
        type="button"
        className="k-btn k-btn--secondary"
        data-testid="claim-withdraw"
        disabled={resolve.isPending}
        onClick={() =>
          resolve.mutate({ resolution: "WITHDRAWN", reason: reason.trim() || undefined })
        }
      >
        {t("claims.resolve.withdraw")}
      </button>
      {resolve.error instanceof ProblemError && (
        <p role="alert" className="k-field__error">
          {resolve.error.problem.detail}
        </p>
      )}
    </form>
  );
}
